import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  signal
} from '@angular/core';
import { CurrentUserService } from '../../../../services/currentUser.service';
import { Job } from '../../../../model/job.model';
import { UserService } from '../../../../services/user.service';
import { MessagingService } from '../../../../components/messaging/messaging.service';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment';
import { ToolbarService } from '../../../../services/toolbar.service';
import { filter } from 'rxjs/operators';
import { DatePipe, NgClass, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { EIO_DATE_UI_FORMAT_PIPE } from '../../../../consts/localization.const';
import { ModalConfigsHelper } from '../../../../helpers/modal-configs.helper';
import { ConfirmBoxComponent } from '../../../../components/confirm-box/confirm-box.component';
import { smallModalConfig } from '../../../../consts/modal-config.const';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JobService } from '../../../../services/job.service';
import { MatCard } from '@angular/material/card';
import { RouterLink } from '@angular/router';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DaysSincePipe } from '../../../../pipes/days-since.pipe';

@Component({
  selector: 'app-job-card',
  templateUrl: './job-card.component.html',
  styleUrls: ['./job-card.component.scss'],
  providers: [DatePipe],
  imports: [
    MatCard,
    NgClass,
    RouterLink,
    MatTooltip,
    MatIcon,
    MatIconButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    NgTemplateOutlet,
    MatButton,
    MatProgressSpinner,
    UpperCasePipe,
    DaysSincePipe
  ]
})
export class JobCardComponent implements OnInit, AfterViewInit, OnChanges {
  job = input.required<Job>();
  bucket = input.required<string>();
  updateBuckets = output<boolean>();

  timezone: string;
  dateInBucket: string;
  photoLoaded = {};
  activeJobId: number;
  largeNumberDays = false;
  relevantDate = '';

  nextButtonPending = signal(false);
  prevButtonPending = signal(false);

  #destroyRef = inject(DestroyRef);
  #elRef = inject(ElementRef);
  #matDialog = inject(MatDialog);
  #currentUserService = inject(CurrentUserService);
  #jobService = inject(JobService);
  #message = inject(MessagingService);
  #toolbarService = inject(ToolbarService);
  userService = inject(UserService);
  #datePipe = inject(DatePipe);
  #modalConfigsHelper = inject(ModalConfigsHelper);

  ngOnInit(): void {
    this.timezone = this.#currentUserService?.timeZone;
    this.#setDateInBucket();
    this.#getActiveJobId();
  }

  ngOnChanges(): void {
    this.prevButtonPending.set(false);
    this.nextButtonPending.set(false);
  }

  ngAfterViewInit(): void {
    if (this.activeJobId === this.job().jobId) {
      setTimeout(() => {
        this.#elRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }, 200);
    }
  }

  setTextAnimation(parent: any, child: any): boolean {
    return child.offsetWidth > parent.offsetWidth;
  }

  #getActiveJobId(): void {
    this.#jobService.activeJobId$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((activeJobId) => {
      this.activeJobId = activeJobId;
    });
  }

  moveToNextBucket(): void {
    this.nextButtonPending.set(true);
    this.userService
      .moveToNextRepairState(this.job().jobId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: () => {
          this.#message.error(`Could not move job to next bucket.`);
          this.nextButtonPending.set(false);
        },
        complete: () => {
          this.saveActiveJob(this.job().jobId);
          this.updateBuckets.emit(true);
        }
      });
  }

  moveToPreviousBucket(): void {
    this.prevButtonPending.set(true);
    this.userService
      .moveToPreviousRepairState(this.job().jobId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.saveActiveJob(this.job().jobId);
          this.updateBuckets.emit(true);
        },
        error: () => {
          this.#message.error(`Could not move job to previous bucket.`);
          this.prevButtonPending.set(false);
        }
      });
  }

  #setDateInBucket(): void {
    switch (this.bucket()) {
      case 'REPAIR_BOOKED':
        this.dateInBucket = this.job().vehicleDateIn;
        this.relevantDate = `Vehicle Scheduled In For: ${this.#datePipe.transform(
          this.job().vehicleDateIn,
          EIO_DATE_UI_FORMAT_PIPE
        )}`;
        break;
      case 'VEHICLE_IN':
        this.dateInBucket = this.job().vehicleDateIn;
        this.relevantDate = `Vehicle In: ${this.#datePipe.transform(this.job().vehicleDateIn, EIO_DATE_UI_FORMAT_PIPE)}`;
        break;
      case 'REPAIR_STARTED':
        this.dateInBucket = this.job().repairStartedDate;
        this.relevantDate = `Repair Started On: ${this.#datePipe.transform(
          this.job().repairStartedDate,
          EIO_DATE_UI_FORMAT_PIPE
        )}`;
        break;
      case 'REPAIR_COMPLETED':
        this.dateInBucket = this.job().repairCompletedDate;
        this.relevantDate = `Repair Completed On: ${this.#datePipe.transform(
          this.job().repairCompletedDate,
          EIO_DATE_UI_FORMAT_PIPE
        )}`;
        break;
      case 'VEHICLE_OUT':
        this.dateInBucket = this.job().vehicleDateOut;
        this.relevantDate = `Vehicle Out: ${this.#datePipe.transform(this.job().vehicleDateOut, EIO_DATE_UI_FORMAT_PIPE)}`;
        break;
    }

    const dateEntered = this.timezone
      ? moment(this.dateInBucket).tz(this.timezone).valueOf()
      : moment(this.timezone).valueOf();
    const today = moment(Date.now()).unix();
    const diff = today - dateEntered;
    const days = Math.floor(diff / (3600 * 24));
    this.largeNumberDays = days > 99;
  }

  saveActiveJob(jobId: number): void {
    this.#jobService.activeJobId = jobId;
    sessionStorage.setItem('previousUrl', 'jobViewer');
  }

  archiveJob(): void {
    const data = {
      title: 'Archive',
      alertMessage: 'Are you sure you want to archive this claim?',
      confirmButton: 'ARCHIVE',
      hideCannotBeUndone: true
    };
    const dialogRef = this.#matDialog.open(
      ConfirmBoxComponent,
      this.#modalConfigsHelper.buildMediumModalConfig(data, smallModalConfig)
    );
    dialogRef
      .afterClosed()
      .pipe(filter((value) => value === 'ARCHIVE'))
      .subscribe({
        next: () => {
          this.#toolbarService.setCloudState('SAVING');
          this.userService
            .archiveJobManually(this.job().jobId)
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe({
              next: () => {
                this.#message.confirm('Job archived');
                this.#toolbarService.setCloudState('RESTING');
                this.updateBuckets.emit(true);
              },
              error: () => {
                this.#message.error('Could not archived the job.');
                this.#toolbarService.setCloudState('RESTING');
              }
            });
        }
      });
  }

  toggleJobReadStatus(isUnread: boolean, id: number): void {
    this.userService.toggleJobReadStatus(isUnread, id).subscribe(() => this.updateBuckets.emit(true));
  }
}
